import { combineReducers, type UnknownAction } from '@reduxjs/toolkit';
import { adminEmailDeliveriesReducer } from '@/features/admin/store/admin-email-deliveries.slice';
import { adminStatsReducer } from '@/features/admin/store/admin-stats.slice';
import { staffAccessReducer } from '@/features/access/store/staff-access.slice';
import { staffSessionsReducer } from '@/features/access/store/staff-sessions.slice';
import { staffAccountsReducer } from '@/features/admin/store/staff-accounts.slice';
import { adminReducer } from '@/features/admin/store/admin.slice';
import {
  authReducer,
  sessionExpired,
  signOut,
  staffSetup,
  staffVerify,
} from '@/features/auth/store/auth.slice';
import { claimsReducer } from '@/features/claims/store/claims.slice';
import { creditsReducer } from '@/features/organizations/store/credits.slice';
import { organizationPostEventReducer } from '@/features/organizations/store/organization-post-event.slice';
import { organizationReportsReducer } from '@/features/organizations/store/organization-reports.slice';
import { organizationsReducer } from '@/features/organizations/store/organizations.slice';
import { referralCodesReducer } from '@/features/referral-codes/store/referral-codes.slice';
import { writeKeysReducer } from '@/features/organizations/store/write-keys.slice';
import { salesInquiriesReducer } from '@/features/sales/store/sales-inquiries.slice';

const appReducer = combineReducers({
  admin: adminReducer,
  // Genel görünüm panosu — ayrı dal (dosya tavanı bölmesi, aynı özellik klasörü).
  adminStats: adminStatsReducer,
  // E-posta gönderim görünümü — salt okunur, kendi dalı.
  adminEmailDeliveries: adminEmailDeliveriesReducer,
  auth: authReducer,
  claims: claimsReducer,
  staffAccess: staffAccessReducer,
  // Personel hesapları (yalnız admin) ve personelin kendi oturumları.
  staffAccounts: staffAccountsReducer,
  staffSessions: staffSessionsReducer,
  // Satış adayları, kurumlar ve kurum kredisi (kurum satışla açılır · P1-P6).
  panelSales: salesInquiriesReducer,
  panelOrgs: organizationsReducer,
  panelCredits: creditsReducer,
  // Kurum detayının etkinlik raporları özeti (organizatör raporu Story 5), salt okunur.
  panelOrgReports: organizationReportsReducer,
  // Kurum detayının etkinlik sonrası bölümü ve "Anketi kapat" (belge ve anket Story 8).
  panelOrgPostEvent: organizationPostEventReducer,
  // Referans (hediye) kodları (P7).
  panelReferral: referralCodesReducer,
  // Yazma diyaloglarının yineleme anahtarları (kapat-aç arasında korunur).
  panelWriteKeys: writeKeysReducer,
});

export type RootState = ReturnType<typeof appReducer>;

/**
 * KÖK SIFIRLAMA (QA 7A P1 sınıfı — web ve mobil ile AYNI desen).
 *
 * Oturum sınırı geçildiğinde TÜM dilimler başlangıç durumuna döner: çıkış
 * sunucuda tamamlandığında ve YENİ oturum açılışında. Panel tek sekmede
 * hesap değiştirebilir; önceki yöneticinin verisi ekranda kalamaz.
 *
 * `signOut.failure` BİLİNÇLİ OLARAK YOK: HttpOnly çerez istemciden silinemez,
 * başarısız çıkışta sunucu oturumu canlıdır ve arayüzü "çıkıldı"ya çevirmek
 * yalan olurdu.
 */
export const SESSION_BOUNDARY_ACTIONS: readonly string[] = [
  signOut.success.type,
  staffVerify.success.type,
  /*
   * İLK KURULUM DA BİR OTURUM SINIRIDIR (panel kimliği ayrımı · S2): davet
   * jetonu tüketildiğinde sunucu oturum açar. Bu satır olmasaydı, aynı sekmede
   * önce bir personel çıkıp sonra davetle giren başka bir personel önceki
   * kişinin tablo verisini görebilirdi.
   */
  staffSetup.success.type,
  /*
   * SUNUCU OTURUMU BİTİRDİ (panel küçük işler 1 · B-02): yenileme reddedildi.
   * Önceki oturumun tablo verisi giriş ekranının arkasında ve sonraki girişte
   * kalamaz.
   */
  sessionExpired.type,
];

export const rootReducer = (state: RootState | undefined, action: UnknownAction): RootState => {
  if (state && SESSION_BOUNDARY_ACTIONS.includes(action.type)) {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};
